import { useState } from "react";
import { useNavigate, useParams } from "react-router";
import { get } from "../api/client";
import { useLoad, usePoll } from "../api/hooks";
import type { ClipList, Player, Roster, Video } from "../api/types";
import { useAuth } from "../auth/AuthContext";
import { PlayersStatus } from "../components/PlayersStatus";
import { Shell } from "../components/Shell";
import { Gate } from "../components/Gate";
import { ErrorLine, Face, RoleIcon } from "../components/ui";
import { plural } from "../lib/format";
import { countFor, identityBusy, resolveRoster, type ResolvedRoster } from "../lib/roster";
import { useIsDesktop } from "../lib/device";

type Open = "bat" | "bowl" | null;

/**
 * 06 · Choose players (chosen B, white). Two tiles; tapping one drops a single
 * column of players under THAT tile while the other stays tappable; one column
 * open at a time. The batsman column is open when the page opens, so the
 * players' pictures show without a tap. Free sees the tiles and the price
 * sheet (08 A). Each player is one group the player-identity stage found,
 * shown by ONE picture; until the stage has answered the columns say where it is.
 */
export default function RosterPage() {
  const { id = "" } = useParams();
  const nav = useNavigate();
  const desktop = useIsDesktop();
  const { plan } = useAuth();
  const video = useLoad(() => get<{ video: Video }>(`/v1/videos/${id}`).then((r) => r.video), [id]);
  const clips = useLoad(() => get<ClipList>(`/v1/videos/${id}/deliveries`), [id]);
  const rosterApi = useLoad(() => get<Roster>(`/v1/videos/${id}/players`), [id]);
  const items = clips.data?.items ?? [];
  const roster = resolveRoster(rosterApi.data);
  usePoll(rosterApi.reload, 10_000, identityBusy(roster));
  const selectable = clips.data?.players.selectable ?? plan !== "free";
  const [open, setOpen] = useState<Open>("bat");
  const [bat, setBat] = useState<string | null>(null);
  const [bowl, setBowl] = useState<string | null>(null);
  const [gate, setGate] = useState(false);

  const tap = (which: Open) => {
    if (!selectable) return setGate(true);
    setOpen((o) => (o === which ? null : which));
  };
  const pick = (which: "bat" | "bowl", p: Player) => {
    if (which === "bat") setBat((b) => (b === p.id ? null : p.id));
    else setBowl((b) => (b === p.id ? null : p.id));
    setOpen(null);
  };
  const count = countFor(roster, bat, bowl, items);
  const selectionName = bat && bowl ? `${roster.name(bowl)} to ${roster.name(bat)}` : bat ? roster.name(bat) : bowl ? roster.name(bowl) : "Any";
  const go = () => nav(`/videos/${id}/clips?${new URLSearchParams({ ...(bat ? { batsman: bat } : {}), ...(bowl ? { bowler: bowl } : {}) }).toString()}`);
  const clear = () => { setBat(null); setBowl(null); setOpen("bat"); };
  // Free cannot pick, so no column shows for it; tapping a tile opens the gate.
  const shown: Open = selectable ? open : null;
  const hasPick = !!(bat || bowl);

  // The column is open on arrival, so before the players are found it stays short:
  // PlayersStatus under the tiles says why and offers Find players.
  const emptyText = roster.identity?.state === "ready" ? "No players found in this video." : "No players yet.";
  const tiles = (
    <div className="grid2" style={{ alignItems: "start" }} data-testid="roster">
      <div>
        <Tile role="batsman" chosen={bat ? roster.batsmen.find((p) => p.id === bat) ?? null : null} name={roster.name} open={shown === "bat"} onClick={() => tap("bat")} testid="tile-batsman" />
        {shown === "bat" && <Column players={roster.batsmen} sel={bat} onPick={(p) => pick("bat", p)} roster={roster} loading={clips.loading || rosterApi.loading} empty={emptyText} />}
      </div>
      <div>
        <Tile role="bowler" chosen={bowl ? roster.bowlers.find((p) => p.id === bowl) ?? null : null} name={roster.name} open={shown === "bowl"} onClick={() => tap("bowl")} testid="tile-bowler" />
        {shown === "bowl" && <Column players={roster.bowlers} sel={bowl} onPick={(p) => pick("bowl", p)} roster={roster} loading={clips.loading || rosterApi.loading} empty={emptyText} />}
      </div>
    </div>
  );
  const foot = (
    <>
      {hasPick && (
        <div className="row between" style={{ marginBottom: 10 }}>
          <div><div className="med">{selectionName}</div><div className="tiny">{plural(count, "clip")}{bat && !bowl ? " as batsman" : bowl && !bat ? " as bowler" : ""}</div></div>
          {bat && !bowl && <span className="tiny">+ a bowler for the match-up</span>}
          {bowl && !bat && <span className="tiny">+ a batsman for the match-up</span>}
        </div>
      )}
      <button className="btn dark full" disabled={!hasPick || count === 0} onClick={go} data-testid="roster-continue">{hasPick ? `Continue · ${plural(count, "clip")}` : "Pick a player"}</button>
    </>
  );
  const statusNote = <PlayersStatus videoId={id} roster={roster} selectable={selectable} onChange={rosterApi.reload} className="mt10" />;

  return (
    <Shell title="Choose players" back={`/videos/${id}`} right={<button className="r" onClick={clear}>Clear</button>} foot={!desktop && foot}>
      <div className="p" style={{ paddingTop: desktop ? 32 : 8, paddingBottom: 24 }}>
        {desktop && <><div className="tiny">{video.data?.title}</div><h1 className="mt6" style={{ fontSize: 32 }}>Choose players</h1></>}
        <ErrorLine error={clips.error} />
        {desktop ? (
          <div className="mt32" style={{ display: "grid", gridTemplateColumns: "640px 1fr", gap: 48, alignItems: "start" }}>
            <div>{tiles}{statusNote}</div>
            <div style={{ paddingTop: 8 }}>
              <div className="cap">Selection</div>
              <div className="mt10" style={{ fontSize: 24, fontWeight: 500 }}>{selectionName}</div>
              <p className="sub mt6">Order is the order players first appear in the video. Tiles stay tappable while a column is open.</p>
              <div className="mt24">{foot}</div>
            </div>
          </div>
        ) : (
          <>
            <p className="tiny" style={{ marginBottom: 12 }}>Tap a tile. One player for their balls, one of each for the match-up.</p>
            {tiles}
            {statusNote}
          </>
        )}
      </div>
      {gate && <Gate onClose={() => setGate(false)} onKeep={() => nav(`/videos/${id}`)} />}
    </Shell>
  );
}

function Tile({ role, chosen, open, onClick, name, testid }: { role: "batsman" | "bowler"; chosen: Player | null; open: boolean; onClick: () => void; name: (id: string) => string; testid: string }) {
  return (
    <button type="button" className={`rt${open ? " open" : ""}`} onClick={onClick} data-testid={testid} aria-expanded={open}>
      {chosen ? <Face id={chosen.id} src={chosen.crop_url} css /> : <RoleIcon role={role} />}
      <div>
        <div className="lb">{role === "batsman" ? "Batsman" : "Bowler"}</div>
        <div className={`vv${chosen ? "" : " any"}`}>{chosen ? name(chosen.id) : "Any"}</div>
        <div className="tiny">{chosen ? `${plural(chosen.clip_count, "clip")} · change` : open ? "choose below" : "tap to choose"}</div>
      </div>
      <span className="ar">›</span>
    </button>
  );
}

/** The players as cells: each picture as large as the cell allows, the name under it, the clip count at the bottom right. */
function Column({ players, sel, onPick, roster, loading, empty }: { players: Player[]; sel: string | null; onPick: (p: Player) => void; roster: ResolvedRoster; loading: boolean; empty: string }) {
  if (players.length === 0) return <div className="col" data-testid="roster-column" data-empty="1"><div className="col-empty tiny">{loading ? "Loading…" : empty}</div></div>;
  return (
    <div className="col" data-testid="roster-column">
      {players.map((p) => (
        <button type="button" key={p.id} className={`pcell${p.id === sel ? " sel" : ""}`} onClick={() => onPick(p)} aria-pressed={p.id === sel}>
          <Face id={p.id} src={p.crop_url} css fill />
          <span className="nm">{roster.name(p.id)}</span>
          <span className="ct">{plural(p.clip_count, "clip")}</span>
        </button>
      ))}
    </div>
  );
}
